import useLocale from 'next-translate/useTranslation';
import { ChangeEvent } from 'react';
import React from 'react';
import { FaCrown } from 'react-icons/fa6';

import { SimpleTooltip } from '@/components/ui/tooltip';

import {
  videoModelCanUseReferenceImages,
  videoModelsWithDescription,
  VideoModelType,
} from '@/constants/videoModels';

interface VideoModelSelectionProps {
  onChange: (value: VideoModelType) => void;
  selectedModel: VideoModelType;
  hasImage?: boolean;
  hasTailImage?: boolean;
  hasReferenceImages?: boolean;
  hasReferenceVideo?: boolean;
  /** Length of the attached reference video; hides models whose window is shorter. */
  referenceVideoSeconds?: number | null;
  /** Reference audio attached — only Seedance 2.0 / MiniMax H3 accept it. */
  hasReferenceAudio?: boolean;
  isFreePlan?: boolean;
}

export function VideoModelSelection({
  onChange,
  selectedModel,
  hasImage = false,
  hasTailImage = false,
  hasReferenceImages = false,
  hasReferenceVideo = false,
  referenceVideoSeconds = null,
  hasReferenceAudio = false,
  isFreePlan = false,
}: VideoModelSelectionProps) {
  const { t } = useLocale('ai-videos');

  const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value as VideoModelType);
  };

  const filteredModels = videoModelsWithDescription.filter((model) => {
    // Hide coming soon models from selection
    if ('comingSoon' in model && model.comingSoon) {
      return false;
    }
    // Exclude motion control models from regular video creation
    if (model.options.isMotionControl) {
      return false;
    }
    // When both start and end images are uploaded, only show models that support tail image
    if (hasImage && hasTailImage) {
      return (
        model.options.inputType.includes('image') &&
        model.options.supportsTailImage === true
      );
    }
    if (hasImage) {
      // When only start image is uploaded, show only image-to-video models
      return model.options.inputType.includes('image');
    }
    // When a reference video is attached, only models that accept one work
    // (Seedance 2.0/2.5 Reference, MiniMax H3 Reference, Gemini Omni Flash
    // Reference) — narrowed further by whatever else is attached, since the
    // catalogue's video-capable models don't all take images and audio.
    if (hasReferenceVideo) {
      return (
        model.options.supportsReferenceVideo === true &&
        (!hasReferenceImages ||
          model.options.supportsReferenceImages === true) &&
        (!hasReferenceAudio || model.options.supportsReferenceAudio === true) &&
        // A 25s clip only fits the models whose window reaches it (Seedance
        // 2.5 Reference); shorter clips keep every video-capable model.
        (referenceVideoSeconds === null ||
          (model.options.referenceVideoConfig?.maxClipSeconds ?? 0) + 0.5 >=
            referenceVideoSeconds)
      );
    }
    // When reference images are uploaded (without start image), show only text-to-video models that support reference images
    if (hasReferenceImages) {
      return (
        model.options.supportsReferenceImages === true &&
        model.options.inputType.includes('text') &&
        // With audio also attached, narrow to models that accept it.
        (!hasReferenceAudio || model.options.supportsReferenceAudio === true)
      );
    }
    // When only reference audio is attached, show the models that accept it.
    // Audio always requires an image reference, so the model must also be
    // able to take one — itself or via its reference sibling (excludes
    // Seedance Turbo, which supports audio but has no reference path).
    // Reference-only variants (MiniMax H3 Reference) stay visible only while
    // selected, so the auto-switched model doesn't render as "nothing picked".
    if (hasReferenceAudio) {
      return (
        model.options.supportsReferenceAudio === true &&
        model.options.inputType.includes('text') &&
        videoModelCanUseReferenceImages(model) &&
        (!model.options.requiresReferenceImages ||
          model.title === selectedModel)
      );
    }
    // When no image is uploaded, show all text-to-video models (excluding those that require reference images)
    return (
      model.options.inputType.includes('text') &&
      !model.options.requiresReferenceImages
    );
  });

  // Pin the MiniMax H3 variants (the default model) to the very top in every
  // context (text / image / reference), then the Seedance 2.0 Fast variants,
  // then the Mini variants, then Seedance 2.5, then Kling 4.0, then FLUX 3 /
  // FLUX 3 Max, then WAN 3.0; then free-accessible models first for free
  // users. The FIRST pin must stay in sync with `defaultTextModel` in
  // ai-videos.tsx so the preselected model is always the first card — later
  // pins are just prominence.
  // Array.sort is stable, so the original order is otherwise preserved.
  const isMiniSeedance = (m: (typeof filteredModels)[number]) =>
    m.model.startsWith('bytedance/seedance-2.0-mini/');
  const isFastSeedance = (m: (typeof filteredModels)[number]) =>
    m.model.startsWith('bytedance/seedance-2.0/fast/');
  const isSeedance25 = (m: (typeof filteredModels)[number]) =>
    m.model.startsWith('bytedance/seedance-2.5/');
  const isMiniMaxH3 = (m: (typeof filteredModels)[number]) =>
    m.model.startsWith('minimax/h3/');
  const isKling40 = (m: (typeof filteredModels)[number]) =>
    m.model.startsWith('kwaivgi/kling-v4.0/');
  // FLUX 3 (WaveSpeed) and FLUX 3 Max (fal) — different vendor spellings.
  const isFlux3 = (m: (typeof filteredModels)[number]) =>
    m.model.startsWith('black-forest-labs/flux-3/') ||
    m.model.startsWith('blackforestlabs/flux-3-max/');
  const isWan3 = (m: (typeof filteredModels)[number]) =>
    m.model.startsWith('alibaba/wan-3.0/');
  const sortedModels = [...filteredModels].sort((a, b) => {
    const aH3 = isMiniMaxH3(a);
    const bH3 = isMiniMaxH3(b);
    if (aH3 !== bH3) return aH3 ? -1 : 1;
    const aFast = isFastSeedance(a);
    const bFast = isFastSeedance(b);
    if (aFast !== bFast) return aFast ? -1 : 1;
    const aMini = isMiniSeedance(a);
    const bMini = isMiniSeedance(b);
    if (aMini !== bMini) return aMini ? -1 : 1;
    const a25 = isSeedance25(a);
    const b25 = isSeedance25(b);
    if (a25 !== b25) return a25 ? -1 : 1;
    const aKling40 = isKling40(a);
    const bKling40 = isKling40(b);
    if (aKling40 !== bKling40) return aKling40 ? -1 : 1;
    const aFlux = isFlux3(a);
    const bFlux = isFlux3(b);
    if (aFlux !== bFlux) return aFlux ? -1 : 1;
    const aWan3 = isWan3(a);
    const bWan3 = isWan3(b);
    if (aWan3 !== bWan3) return aWan3 ? -1 : 1;
    if (isFreePlan) {
      if (a.freeUserAccess && !b.freeUserAccess) return -1;
      if (!a.freeUserAccess && b.freeUserAccess) return 1;
    }
    return 0;
  });

  return (
    <fieldset>
      <div className='max-h-[318px] space-y-5 overflow-y-auto'>
        {sortedModels.map((model) => {
          const isLocked = isFreePlan && !model.freeUserAccess;

          return (
            <div key={model.id} className='relative flex items-start ps-1'>
              <div className='flex h-6 items-center'>
                <input
                  id={model.id}
                  aria-describedby={`${model.id}-description`}
                  name='videoModel'
                  type='radio'
                  value={model.title}
                  checked={model.title === selectedModel}
                  onChange={handleOnChange}
                  disabled={isLocked}
                  className={`h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600 ${
                    isLocked
                      ? 'cursor-not-allowed opacity-50'
                      : 'cursor-pointer'
                  }`}
                />
              </div>
              <div className='ms-3 text-sm leading-6'>
                <label
                  htmlFor={model.id}
                  className={`flex items-center font-medium ${
                    isLocked
                      ? 'cursor-not-allowed text-gray-400 dark:text-slate-600'
                      : 'cursor-pointer text-gray-900 dark:text-slate-400'
                  }`}
                >
                  {model.icon &&
                    React.createElement(model.icon, {
                      className: `w-4 h-4 me-2 ${isLocked ? 'opacity-50' : ''}`,
                    })}
                  {model.title}
                  {model.isNew && !isLocked && (
                    <span className='ms-1 rounded-full bg-orange-500 px-1.5 py-0.5 align-middle text-xs text-white'>
                      {t('New')}
                    </span>
                  )}
                  {'isFast' in model && model.isFast && !isLocked && (
                    <span className='ms-1 rounded-full bg-emerald-500 px-1.5 py-0.5 align-middle text-xs text-white'>
                      {t('Fast')}
                    </span>
                  )}
                  {'isPromo' in model && model.isPromo && !isLocked && (
                    <span className='ms-1 rounded-full bg-rose-500 px-1.5 py-0.5 align-middle text-xs text-white'>
                      {t('Launch price')}
                    </span>
                  )}
                  {'isEnterprise' in model &&
                    model.isEnterprise &&
                    !isLocked && (
                      <span className='ms-1 rounded-full bg-indigo-600 px-1.5 py-0.5 align-middle text-xs text-white'>
                        {t('Enterprise')}
                      </span>
                    )}
                  {isLocked && (
                    <SimpleTooltip title={t('Upgrade to use this model')}>
                      <span>
                        <FaCrown className='ms-1.5 h-3.5 w-3.5 text-amber-400' />
                      </span>
                    </SimpleTooltip>
                  )}
                </label>
              </div>
            </div>
          );
        })}
      </div>
    </fieldset>
  );
}
